import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useTexture, Text } from '@react-three/drei';

export function TarotCard({ position, imageUrl, cardTitle, isFlipped, onClick, narrative }) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [rotationY, setRotationY] = useState(0);

  // Animate the card flip and hover effects
  useFrame((state, delta) => {
    if (meshRef.current) {
      // Smooth rotation animation
      const targetRotation = isFlipped ? Math.PI : 0;
      meshRef.current.rotation.y += (targetRotation - meshRef.current.rotation.y) * 0.15;
      
      // Hover animation
      const targetY = hovered ? 0.3 : 0;
      meshRef.current.position.y += (targetY - meshRef.current.position.y) * 0.1;
      
      // Mystical floating animation
      meshRef.current.position.y += Math.sin(state.clock.elapsedTime * 0.5) * 0.02;
    }
  });

  const frontTexture = useTexture(imageUrl);
  const backTexture = useTexture('/card-back.png');

  return (
    <group 
      ref={meshRef} 
      position={position}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {/* Card Back (Face Down) */}
      <mesh rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[3, 5.25]} />
        <meshStandardMaterial 
          map={backTexture} 
          emissive="#1a0b2e"
          emissiveIntensity={0.1}
        />
        {/* Mystical border on back */}
        <mesh position={[0, 0, 0.01]}>
          <planeGeometry args={[3.1, 5.35]} />
          <meshBasicMaterial 
            color="#8B5CF6" 
            transparent 
            opacity={0.3}
            side={2}
          />
        </mesh>
      </mesh>

      {/* Card Front (Face Up) */}
      <mesh>
        <planeGeometry args={[3, 5.25]} />
        <meshStandardMaterial 
          map={frontTexture}
          emissive="#1a0b2e"
          emissiveIntensity={0.05}
        />
        {/* Mystical glow effect */}
        <mesh position={[0, 0, 0.01]}>
          <planeGeometry args={[3.1, 5.35]} />
          <meshBasicMaterial 
            color="#8B5CF6" 
            transparent 
            opacity={isFlipped ? 0.2 : 0}
            side={2}
          />
        </mesh>
      </mesh>

      {/* Card Title Text (only visible when face down) */}
      {!isFlipped && (
        <Text
          position={[0, 0, 0.02]}
          rotation={[0, Math.PI, 0]}
          fontSize={0.4}
          color="#F59E0B"
          anchorX="center"
          anchorY="middle"
        >
          {cardTitle}
        </Text>
      )}

      {/* Mystical particles around the card */}
      {isFlipped && (
        <group>
          {[...Array(8)].map((_, i) => (
            <mesh
              key={i}
              position={[
                Math.cos((i / 8) * Math.PI * 2) * 2,
                Math.sin((i / 8) * Math.PI * 2) * 2,
                0.1
              ]}
            >
              <sphereGeometry args={[0.02, 8, 8]} />
              <meshBasicMaterial 
                color="#8B5CF6" 
                transparent 
                opacity={0.6}
              />
            </mesh>
          ))}
        </group>
      )}

      {/* Mystical energy lines */}
      {isFlipped && (
        <group>
          {[...Array(3)].map((_, i) => (
            <mesh
              key={`line-${i}`}
              position={[0, 0, 0.05]}
              rotation={[0, 0, (i * Math.PI * 2) / 3]}
            >
              <planeGeometry args={[0.1, 4]} />
              <meshBasicMaterial 
                color="#06B6D4" 
                transparent 
                opacity={0.4}
              />
            </mesh>
          ))}
        </group>
      )}
    </group>
  );
}
